import React, { useState, useEffect } from 'react';
import {
    Box, Paper, Typography, Button, Autocomplete, TextField,
    CircularProgress, Alert, Snackbar, FormControl, InputLabel, Select,
    MenuItem, useTheme, useMediaQuery, IconButton, Tooltip, Chip, Stack
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import EditNoteIcon from '@mui/icons-material/EditNote';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import api from '../services/api';
import { isVisualizador } from '../services/auth';
import RichTextEditor from './RichTextEditor';
import BuscaPaciente from './BuscaPaciente';
import DocumentoPreview from './DocumentoPreview';

const FORMAS_PAGAMENTO = [
    { value: 'PIX', label: 'PIX' },
    { value: 'DINHEIRO', label: 'Dinheiro' },
    { value: 'CARTAO', label: 'Cartão' },
];

const FRONTEND_URL = window.location.origin;

function CampoStatus({ ok, label }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {ok
                ? <CheckCircleIcon sx={{ fontSize: '1rem', color: '#1ABB9C' }} />
                : <RadioButtonUncheckedIcon sx={{ fontSize: '1rem', color: '#ccc' }} />}
            <Typography variant="caption" sx={{ color: ok ? '#1ABB9C' : 'text.disabled', fontWeight: ok ? 600 : 400 }}>
                {label}
            </Typography>
        </Box>
    );
}

/** Estado inicial da coluna da direita: nenhum modelo para renderizar ainda. */
function PreviewVazio() {
    return (
        <Box sx={{
            bgcolor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 2,
            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
            minHeight: 480,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            py: 8,
        }}>
            <DescriptionIcon sx={{ fontSize: '3rem', color: '#e0e0e0' }} />
            <Typography color="text.disabled" variant="body2" textAlign="center">
                Configure o modelo deste tipo em<br /><strong>Modelos de Documentos</strong>.
            </Typography>
        </Box>
    );
}

function LinkCompartilhar({ documento, nomePaciente }) {
    const [copiado, setCopiado] = useState(false);
    const link = `${FRONTEND_URL}/aceite/${documento.tokenAceite}`;

    const copiar = () => {
        navigator.clipboard.writeText(link);
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2000);
    };

    const mensagemWhats = encodeURIComponent(
        `Olá, ${nomePaciente}! 👋\n\n` +
        `Segue o link para leitura e assinatura eletrônica do seu *${documento.finalidade}*.\n\n` +
        `🔗 ${link}\n\n` +
        `Por favor, leia com atenção e assine para confirmar o aceite. ✅`
    );

    return (
        <Box sx={{ mt: 2, p: 2.5, bgcolor: '#f0fdf4', border: '1px solid #86efac', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <CheckCircleIcon sx={{ color: '#16a34a', fontSize: '1.1rem' }} />
                <Typography fontWeight={700} color="#16a34a" variant="body2">
                    Documento gerado!
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Nº {documento.numeroDocumento}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
                <TextField
                    value={link}
                    size="small"
                    fullWidth
                    InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.75rem', bgcolor: '#fff' } }}
                />
                <Tooltip title={copiado ? 'Copiado!' : 'Copiar link'}>
                    <IconButton onClick={copiar} color={copiado ? 'success' : 'default'} size="small">
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <Button
                fullWidth
                variant="contained"
                startIcon={<WhatsAppIcon />}
                href={`https://wa.me/?text=${mensagemWhats}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1ebe5d' }, color: '#fff', textTransform: 'none' }}
            >
                Enviar via WhatsApp
            </Button>
        </Box>
    );
}

/**
 * Confirmação para documento que já sai assinado pelo profissional: não há
 * aceite a coletar, o que o usuário quer em seguida é o PDF.
 */
function DocumentoEmitido({ documento, onErro }) {
    const [baixando, setBaixando] = useState(false);

    const baixar = async () => {
        setBaixando(true);
        try {
            const res = await api.get(`/documentos/${documento.id}/download`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${documento.numeroDocumento || 'documento'}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            onErro('Não foi possível baixar o PDF.');
        } finally {
            setBaixando(false);
        }
    };

    return (
        <Box sx={{ mt: 2, p: 2.5, bgcolor: '#f0fdf4', border: '1px solid #86efac', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <CheckCircleIcon sx={{ color: '#16a34a', fontSize: '1.1rem' }} />
                <Typography fontWeight={700} color="#16a34a" variant="body2">
                    Documento emitido
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Nº {documento.numeroDocumento}
                </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Emitido já assinado pelo profissional — não precisa de aceite do paciente.
                Também fica no Histórico de Documentos.
            </Typography>
            <Button
                fullWidth
                variant="contained"
                startIcon={baixando ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdfIcon />}
                onClick={baixar}
                disabled={baixando}
                sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' }, textTransform: 'none', fontWeight: 700 }}
            >
                {baixando ? 'Baixando...' : 'Baixar PDF'}
            </Button>
        </Box>
    );
}

/**
 * Apresentação de cada tipo emissível por esta tela: ícone, descrição e a chave
 * interna usada nas rotas de emissão.
 *
 * Quais tipos existem e como se chamam vem de `/documentos/metadados`, não
 * daqui — havia quatro listas paralelas de tipo no front e foi assim que
 * encaminhamento e pedido médico ficaram meses no enum sem tela. O que fica
 * local é só o que é visual, mais o recorte dos tipos que *esta* tela emite:
 * RECIBO é emissível pelo sistema, mas sai do financeiro, a partir de um título.
 */
const TIPO_UI = {
    CONTRATO_SERVICO: {
        key: 'contrato',
        icon: <DescriptionIcon fontSize="small" />,
        descricao: 'Formaliza o vínculo terapêutico, plano e forma de pagamento.',
    },
    TERMO_CONSENTIMENTO: {
        key: 'termo',
        icon: <AssignmentIcon fontSize="small" />,
        descricao: 'Declara o sigilo profissional e os limites éticos do atendimento.',
    },
    DECLARACAO: {
        key: 'declaracao',
        icon: <AssignmentIcon fontSize="small" />,
        descricao: 'Comprova atendimento para empregador, escola ou outro destino.',
    },
    ENCAMINHAMENTO: {
        key: 'encaminhamento',
        icon: <ForwardToInboxIcon fontSize="small" />,
        descricao: 'Encaminha o paciente a psiquiatra ou outra especialidade.',
    },
    PEDIDO_MEDICO: {
        key: 'pedido',
        icon: <MedicalInformationIcon fontSize="small" />,
        descricao: 'Solicita avaliação ou exame a outro profissional.',
    },
};

/** Usada só até os metadados chegarem, para a tela não piscar sem opções. */
const TIPOS_FALLBACK = Object.entries(TIPO_UI).map(([valor, ui]) => ({
    valor, key: ui.key, label: ui.key, icon: ui.icon, descricao: ui.descricao,
}));

/** Tipos que endereçam o documento a outro profissional. */
const TIPOS_COM_DESTINATARIO = ['encaminhamento', 'pedido'];

export default function GeradorDocumento() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const soLeitura = isVisualizador();

    const [tipoSelecionado, setTipoSelecionado] = useState('contrato');

    const [planos, setPlanos] = useState([]);
    const [parametros, setParametros] = useState(null);

    const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
    const [planoSelecionado, setPlanoSelecionado] = useState(null);
    const [formaPagamento, setFormaPagamento] = useState('');
    const [finalidade, setFinalidade] = useState('');
    const [destinatario, setDestinatario] = useState('');
    const [motivo, setMotivo] = useState('');

    const [tipos, setTipos] = useState(TIPOS_FALLBACK);

    // Texto que o profissional editou nesta emissão. null = não mexeu, e aí o
    // corpo vai sem `conteudo` e o servidor usa o modelo salvo.
    const [conteudoEditado, setConteudoEditado] = useState(null);
    const [editando, setEditando] = useState(false);

    const [assinaturaPaciente, setAssinaturaPaciente] = useState(null); // null=não buscado, false=sem ativa, obj=ativa
    const [loadingAssinatura, setLoadingAssinatura] = useState(false);

    const [loadingGerar, setLoadingGerar] = useState(false);
    const [documentoGerado, setDocumentoGerado] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        // Pacientes não entram aqui: o BuscaPaciente consulta o servidor conforme
        // o usuário digita, em vez de a tela baixar o cadastro inteiro no mount.
        Promise.all([
            api.get('/planos-atendimento'),
            api.get('/parametros'),
            api.get('/documentos/metadados'),
        ]).then(([resPlanos, resParametros, resMeta]) => {
            const ta = (d) => Array.isArray(d) ? d : (d?.content ?? []);
            setPlanos(ta(resPlanos.data).filter(p => p.ativo));
            setParametros(resParametros.data);

            // Tipos e rótulos vêm do servidor; o ícone e a descrição são locais.
            // Tipo sem entrada em TIPO_UI não é emitido por esta tela (é o caso
            // do recibo, que sai do financeiro a partir de um título).
            const doServidor = (resMeta.data?.tipos || [])
                .filter(t => t.emissivel && TIPO_UI[t.valor])
                .map(t => ({ valor: t.valor, label: t.rotulo, ...TIPO_UI[t.valor] }));
            if (doServidor.length) setTipos(doServidor);
        }).catch(() => mostrarMensagem('Erro ao carregar dados iniciais.', 'error'));
    }, []);


    const handleGerar = async () => {
        if (tipoSelecionado === 'contrato' && (!pacienteSelecionado || !planoSelecionado || !formaPagamento)) {
            mostrarMensagem('Selecione paciente, plano e forma de pagamento.', 'warning');
            return;
        }
        if (tipoSelecionado !== 'contrato' && !pacienteSelecionado) {
            mostrarMensagem('Selecione um paciente.', 'warning');
            return;
        }
        if (precisaDestinatario && !destinatario.trim()) {
            mostrarMensagem('Informe o destinatário (especialidade ou profissional).', 'warning');
            return;
        }
        setLoadingGerar(true);
        setDocumentoGerado(null);
        try {
            let res;
            if (precisaDestinatario) {
                // Encaminhamento e pedido médico compartilham o corpo; o tipo vem da rota.
                res = await api.post(
                    tipoSelecionado === 'encaminhamento'
                        ? '/documentos/gerar-encaminhamento'
                        : '/documentos/gerar-pedido-medico',
                    {
                        idPaciente: pacienteSelecionado.id_paciente,
                        destinatario: destinatario.trim(),
                        motivo: motivo.trim() || null,
                        conteudo: conteudoEditado,
                    });
            } else if (tipoSelecionado === 'declaracao') {
                // Declaracao tem corpo proprio: finalidade em vez de plano/pagamento.
                res = await api.post('/documentos/gerar-declaracao', {
                    idPaciente: pacienteSelecionado.id_paciente,
                    finalidade: finalidade || null,
                    conteudo: conteudoEditado,
                });
            } else {
                res = await api.post(
                    tipoSelecionado === 'contrato' ? '/documentos/gerar-contrato' : '/documentos/gerar-termo',
                    {
                        idPaciente: pacienteSelecionado.id_paciente,
                        idPlanoAtendimento: planoSelecionado?.id_p_atendimento || null,
                        formaPagamento: formaPagamento || null,
                        conteudo: conteudoEditado,
                    });
            }
            setDocumentoGerado(res.data);
        } catch {
            mostrarMensagem(`Erro ao gerar: ${tipos.find(t => t.key === tipoSelecionado)?.label ?? 'documento'}.`, 'error');
        } finally {
            setLoadingGerar(false);
        }
    };

    const buscarAssinatura = async (idPaciente) => {
        if (!idPaciente) { setAssinaturaPaciente(null); setPlanoSelecionado(null); return; }
        setLoadingAssinatura(true);
        try {
            const res = await api.get(`/assinaturas-paciente/paciente/${idPaciente}/ativa`);
            const assinatura = res.data || false;
            setAssinaturaPaciente(assinatura);
            if (assinatura && assinatura.idPlanoAtendimento) {
                const plano = planos.find(p => p.id_p_atendimento === assinatura.idPlanoAtendimento);
                setPlanoSelecionado(plano ?? null);
            } else {
                setPlanoSelecionado(null);
            }
        } catch {
            setAssinaturaPaciente(false);
            setPlanoSelecionado(null);
        } finally {
            setLoadingAssinatura(false);
        }
    };

    const mostrarMensagem = (message, severity = 'success') =>
        setSnackbar({ open: true, message, severity });

    const isContrato = tipoSelecionado === 'contrato';
    const isDeclaracao = tipoSelecionado === 'declaracao';
    const precisaDestinatario = TIPOS_COM_DESTINATARIO.includes(tipoSelecionado);

    // Encaminhamento e pedido médico têm modelo embutido no back: a falta de
    // texto configurado não impede a emissão, diferente dos demais tipos.
    const templateAtual = isContrato ? parametros?.textoPadraoContrato
        : isDeclaracao ? parametros?.textoPadraoDeclaracao
        : tipoSelecionado === 'encaminhamento' ? parametros?.textoPadraoEncaminhamento
        : tipoSelecionado === 'pedido' ? parametros?.textoPadraoPedidoMedico
        : (parametros?.textoPadraoTermo || parametros?.textoPadraoDeclaracao);

    // O texto trabalhado aqui é o modelo cru, com os marcadores — quem resolve
    // {{NOME_PACIENTE}} e companhia é o servidor, na prévia e na emissão. A tela
    // não substitui mais nada: fazia 14 dos 44 marcadores, então a prévia
    // divergia do PDF.
    const textoAtual = conteudoEditado ?? (templateAtual || '');

    /** Contexto que o servidor usa para resolver os marcadores na prévia. */
    const dadosPrevia = pacienteSelecionado ? {
        idPaciente: pacienteSelecionado.id_paciente,
        idPlanoAtendimento: planoSelecionado?.id_p_atendimento ?? null,
        formaPagamento: formaPagamento || null,
        finalidade: finalidade || null,
        destinatario: destinatario || null,
        motivo: motivo || null,
    } : null;

    // Agora todos os tipos aceitam `conteudo` na API — contrato e termo também.
    const podeEditarConteudo = true;
    const tipoConfig = tipos.find(t => t.key === tipoSelecionado) ?? tipos[0];

    const campoOkPaciente = !!pacienteSelecionado;
    const campoOkPlano = !!planoSelecionado;
    const campoOkPagamento = !!formaPagamento;
    const campoOkAssinatura = !!assinaturaPaciente;
    const campoOkDestinatario = !!destinatario.trim();
    const podeGerar = isContrato
        ? campoOkPaciente && campoOkPlano && campoOkPagamento && campoOkAssinatura && !!templateAtual
        : precisaDestinatario
            // Sem exigir templateAtual: há modelo padrão no servidor.
            ? campoOkPaciente && campoOkDestinatario
            : campoOkPaciente && !!templateAtual;
    const rotuloModelo = isContrato ? 'contrato'
        : isDeclaracao ? 'declaração'
        : tipoSelecionado === 'encaminhamento' ? 'encaminhamento'
        : tipoSelecionado === 'pedido' ? 'pedido médico'
        : 'termo';

    /** Troca de tipo zera o que é específico do tipo anterior. */
    const selecionarTipo = (key) => {
        setTipoSelecionado(key);
        setDocumentoGerado(null);
        setConteudoEditado(null);
        setEditando(false);
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* Header */}
            <Box sx={{ mb: 3 }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#333' }}>
                    Gerador de Documentos
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Gere contratos e termos com preenchimento automático dos dados do paciente
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start' }}>

                {/* ── COLUNA ESQUERDA: Configuração ── */}
                <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: 360 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                    {/* Seletor de tipo */}
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1.5 }}>
                            Tipo de Documento
                        </Typography>
                        <Stack spacing={1}>
                            {tipos.map(tipo => (
                                <Box
                                    key={tipo.key}
                                    onClick={() => selecionarTipo(tipo.key)}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 1.5,
                                        border: '2px solid',
                                        borderColor: tipoSelecionado === tipo.key ? '#1ABB9C' : 'transparent',
                                        bgcolor: tipoSelecionado === tipo.key ? 'rgba(26,187,156,0.06)' : '#f8f9fa',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                        '&:hover': { borderColor: '#1ABB9C', bgcolor: 'rgba(26,187,156,0.04)' },
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                    }}
                                >
                                    <Box sx={{ color: tipoSelecionado === tipo.key ? '#1ABB9C' : 'text.secondary', mt: 0.2 }}>
                                        {tipo.icon}
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: tipoSelecionado === tipo.key ? '#1ABB9C' : 'text.primary' }}>
                                            {tipo.label}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {tipo.descricao}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </Paper>

                    {/* Campos */}
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 2 }}>
                            Dados
                        </Typography>

                        <Stack spacing={2}>
                            <BuscaPaciente
                                label="Paciente *"
                                value={pacienteSelecionado}
                                onChange={(val) => { setPacienteSelecionado(val); setDocumentoGerado(null); buscarAssinatura(val?.id_paciente); }}
                                disabled={soLeitura}
                                destacado
                            />

                            {isDeclaracao && (
                                <TextField
                                    label="Finalidade"
                                    size="small"
                                    fullWidth
                                    value={finalidade}
                                    onChange={(e) => { setFinalidade(e.target.value); setDocumentoGerado(null); }}
                                    disabled={soLeitura}
                                    placeholder="Empregador, escola, perícia..."
                                    helperText="Sai no documento; em branco usa 'Declaração de atendimento psicológico'"
                                />
                            )}

                            {precisaDestinatario && (
                                <>
                                    <TextField
                                        label="Destinatário *"
                                        size="small"
                                        fullWidth
                                        value={destinatario}
                                        onChange={(e) => { setDestinatario(e.target.value); setDocumentoGerado(null); }}
                                        disabled={soLeitura}
                                        placeholder="Psiquiatria, ou Dr. João Silva — CRM 12345"
                                        helperText="Especialidade ou profissional a quem o documento se destina"
                                        sx={{ '& .MuiOutlinedInput-root': campoOkDestinatario ? { '& fieldset': { borderColor: '#1ABB9C' } } : {} }}
                                    />
                                    <TextField
                                        label={tipoSelecionado === 'encaminhamento' ? 'Motivo do encaminhamento' : 'Solicitação'}
                                        size="small"
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        value={motivo}
                                        onChange={(e) => { setMotivo(e.target.value); setDocumentoGerado(null); }}
                                        disabled={soLeitura}
                                        placeholder={tipoSelecionado === 'encaminhamento'
                                            ? 'Resumo do que motiva o encaminhamento...'
                                            : 'O que está sendo solicitado...'}
                                        helperText="Sai no corpo do documento; em branco imprime 'Não informado.'"
                                    />
                                </>
                            )}

                            <Autocomplete
                                options={planos}
                                getOptionLabel={(o) => o.nome || ''}
                                value={planoSelecionado}
                                onChange={(_, val) => { setPlanoSelecionado(val); setDocumentoGerado(null); }}
                                isOptionEqualToValue={(o, v) => o.id_p_atendimento === v.id_p_atendimento}
                                disabled={soLeitura || isDeclaracao || precisaDestinatario}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Plano de Atendimento"
                                        size="small"
                                        helperText={planoSelecionado
                                            ? `${planoSelecionado.quantidadeSessoes} sessões · R$ ${Number(planoSelecionado.valorTotal ?? planoSelecionado.valorSessaoAvulsa ?? 0).toFixed(2)}`
                                            : isContrato ? 'Obrigatório para contrato' : 'Opcional para termo'}
                                        sx={{ '& .MuiOutlinedInput-root': campoOkPlano ? { '& fieldset': { borderColor: '#1ABB9C' } } : {} }}
                                    />
                                )}
                            />

                            <FormControl fullWidth size="small">
                                <InputLabel>Forma de Pagamento{isContrato ? ' *' : ''}</InputLabel>
                                <Select
                                    value={formaPagamento}
                                    label={`Forma de Pagamento${isContrato ? ' *' : ''}`}
                                    onChange={(e) => { setFormaPagamento(e.target.value); setDocumentoGerado(null); }}
                                    disabled={soLeitura || isDeclaracao || precisaDestinatario}
                                    sx={campoOkPagamento ? { '& fieldset': { borderColor: '#1ABB9C' } } : {}}
                                >
                                    {FORMAS_PAGAMENTO.map(f => (
                                        <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    </Paper>

                    {/* Checklist + Botão */}
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1, display: 'block', mb: 1.5 }}>
                            Requisitos
                        </Typography>
                        <Stack spacing={0.75} sx={{ mb: 2.5 }}>
                            <CampoStatus ok={campoOkPaciente} label="Paciente selecionado" />
                            {isContrato && (
                                <CampoStatus
                                    ok={campoOkAssinatura}
                                    label={
                                        loadingAssinatura ? 'Verificando assinatura...'
                                        : campoOkAssinatura ? `Assinatura ativa · ${assinaturaPaciente.nomePlanoAtendimento}`
                                        : campoOkPaciente ? 'Sem assinatura ativa'
                                        : 'Assinatura ativa'
                                    }
                                />
                            )}
                            {isContrato && <CampoStatus ok={campoOkPlano} label="Plano selecionado" />}
                            {isContrato && <CampoStatus ok={campoOkPagamento} label="Forma de pagamento" />}
                            {precisaDestinatario && <CampoStatus ok={campoOkDestinatario} label="Destinatário informado" />}
                            {/* Para encaminhamento e pedido o modelo é opcional: o
                                servidor tem um texto padrão embutido. */}
                            <CampoStatus
                                ok={!!templateAtual}
                                label={templateAtual || !precisaDestinatario
                                    ? `Modelo de ${rotuloModelo} configurado`
                                    : `Sem modelo de ${rotuloModelo} — usa o texto padrão`}
                            />
                        </Stack>

                        {isContrato && campoOkPaciente && assinaturaPaciente === false && !loadingAssinatura && (
                            <Alert severity="error" sx={{ mb: 2, py: 0.5, fontSize: '0.8rem' }}>
                                Paciente sem assinatura ativa. Contrato exige plano contratado.
                            </Alert>
                        )}

                        {!isContrato && !isDeclaracao && campoOkPaciente && (
                            <Alert severity="info" sx={{ mb: 2, py: 0.5, fontSize: '0.8rem' }}>
                                Termo válido para sessões avulsas — não exige assinatura.
                            </Alert>
                        )}

                        {isDeclaracao && campoOkPaciente && (
                            <Alert severity="info" sx={{ mb: 2, py: 0.5, fontSize: '0.8rem' }}>
                                Declaração numerada automaticamente e emitida já assinada pelo profissional.
                            </Alert>
                        )}

                        {precisaDestinatario && campoOkPaciente && (
                            <Alert severity="info" sx={{ mb: 2, py: 0.5, fontSize: '0.8rem' }}>
                                Numerado automaticamente ({tipoSelecionado === 'encaminhamento' ? 'ENC' : 'PED'})
                                e emitido já assinado pelo profissional.
                            </Alert>
                        )}

                        {!templateAtual && !precisaDestinatario && (
                            <Alert severity="warning" sx={{ mb: 2, py: 0.5, fontSize: '0.8rem' }}>
                                Configure o modelo em <strong>Modelos de Documentos</strong>.
                            </Alert>
                        )}

                        {!soLeitura && (
                            <Button
                                fullWidth
                                variant="contained"
                                size="large"
                                startIcon={loadingGerar
                                    ? <CircularProgress size={18} color="inherit" />
                                    : isContrato ? <PictureAsPdfIcon /> : <AssignmentIcon />}
                                onClick={handleGerar}
                                disabled={!podeGerar || loadingGerar}
                                sx={{
                                    bgcolor: '#1ABB9C',
                                    '&:hover': { bgcolor: '#129A80' },
                                    '&:disabled': { bgcolor: '#e0e0e0' },
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    py: 1.25,
                                }}
                            >
                                {loadingGerar ? 'Gerando...' : `Gerar ${tipoConfig.label}`}
                            </Button>
                        )}
                    </Paper>
                </Box>

                {/* ── COLUNA DIREITA: Preview ── */}
                <Box sx={{ flex: 1, position: { md: 'sticky' }, top: { md: 24 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                            {editando ? 'Editando o documento' : 'Pré-visualização'}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {conteudoEditado != null && (
                                <Chip
                                    label="Texto alterado"
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.68rem', bgcolor: 'rgba(245,158,11,0.15)', color: '#b45309', fontWeight: 700 }}
                                />
                            )}
                            {!conteudoEditado && textoAtual && (
                                <Chip
                                    label="Ao vivo"
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.68rem', bgcolor: 'rgba(26,187,156,0.12)', color: '#1ABB9C', fontWeight: 700 }}
                                />
                            )}

                            {conteudoEditado != null && !soLeitura && (
                                <Tooltip title="Descartar as alterações e voltar ao modelo">
                                    <Button
                                        size="small"
                                        startIcon={<RestartAltIcon fontSize="small" />}
                                        onClick={() => { setConteudoEditado(null); setDocumentoGerado(null); }}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Restaurar modelo
                                    </Button>
                                </Tooltip>
                            )}

                            {podeEditarConteudo && !soLeitura && (
                                <Button
                                    size="small"
                                    variant={editando ? 'contained' : 'outlined'}
                                    startIcon={editando ? <VisibilityIcon fontSize="small" /> : <EditNoteIcon fontSize="small" />}
                                    onClick={() => setEditando(v => !v)}
                                    sx={{
                                        textTransform: 'none',
                                        ...(editando
                                            ? { bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' } }
                                            : { borderColor: '#1ABB9C', color: '#1ABB9C' }),
                                    }}
                                >
                                    {editando ? 'Ver resultado' : 'Editar texto'}
                                </Button>
                            )}
                        </Box>
                    </Box>

                    {editando ? (
                        <>
                            <Alert severity="info" sx={{ mb: 1.5, py: 0.5, fontSize: '0.8rem' }}>
                                A edição vale só para esta emissão — o modelo salvo não muda.
                                Os marcadores ficam à vista aqui e são resolvidos no documento;
                                use <strong>Variáveis</strong> na barra para inserir um sem
                                digitar o nome à mão.
                            </Alert>
                            <RichTextEditor
                                value={textoAtual}
                                onChange={(html) => { setConteudoEditado(html); setDocumentoGerado(null); }}
                                minHeight={420}
                                showVariables
                                pageLook
                                placeholder={`Texto do ${rotuloModelo}...`}
                            />
                        </>
                    ) : textoAtual ? (
                        /* Prévia pelo servidor: o mesmo código que gera o PDF, com os
                           dados reais do paciente escolhido. Sem paciente, o servidor
                           devolve o modelo com texto de exemplo. */
                        <DocumentoPreview html={textoAtual} dados={dadosPrevia} minHeight={520} />
                    ) : (
                        <PreviewVazio />
                    )}

                    {/* Só contrato e termo geram token de aceite; declaração,
                        encaminhamento, pedido e recibo saem já assinados pelo
                        profissional. Sem o token o link de aceite apontaria para
                        /aceite/null, então esses tipos mostram o download. */}
                    {documentoGerado && (documentoGerado.tokenAceite
                        ? (
                            <LinkCompartilhar
                                documento={documentoGerado}
                                nomePaciente={pacienteSelecionado?.nomeCompleto || ''}
                            />
                        ) : (
                            <DocumentoEmitido
                                documento={documentoGerado}
                                onErro={(msg) => mostrarMensagem(msg, 'error')}
                            />
                        ))}
                </Box>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
